import React, { useEffect, useState, useRef } from "react";
import { renderError } from "../../../helpers/errors";
import { createFilterQueryString } from "../../../helpers/helpers";
import { createModalProps, createOptions } from "../../../helpers/selects";
import api from "../../../services/api";
import InputSelect from "../../InputSelect";
import { ModalCreateCompany } from "../../ModalCreateCompany";

const SEARCH_DEBOUNCE_MS = 300;
const MAX_RESULTS_PER_PAGE = 50;

export const SelectCompany = ({label, nullable, selectedCompany, setSelectedCompany, isMulti, changeCompanies, fields, minSearch, create}) => {
    fields = fields ?? ['trade_name', 'cnpj'];
    minSearch = minSearch ?? 1;

    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState('');
    const [noOptionsText, setNoOptionsText] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const debounceRef = useRef(null);

    window.controller = null;

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        if (search.length < minSearch) {
            const diff = minSearch - search.length;
            setNoOptionsText(`Digite mais ${diff} ${diff === 1 ? 'caracter' : 'caracteres'}`);
            handleSetCompanies([], true);
            if (window.requestController) {
                window.requestController.abort();
            }
            return;
        }
        debounceRef.current = setTimeout(() => {
            searchCompanies();
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [search]);
    
    useEffect(() => {
        checkIfNeedToGet();
    }, [selectedCompany]);

    useEffect(() => {
        if(changeCompanies){
            changeCompanies(companies);
        }
    }, [companies]);

    const checkIfNeedToGet = () => {
        let companiesIds = isMulti ? selectedCompany : [selectedCompany];
        let arrayFilters = [];

        companiesIds.map(companyId => {
            if(companyId === null) return;
            if(!companies.some(company => company.id === companyId)){
                arrayFilters.push([
                    `[id][equal]=${companyId}`,
                ]);
            }
        });

        if(arrayFilters.length > 0){
            getCompanies(arrayFilters);
        } else {
            // Para forçar atualização da mensagem "Digite mais x caracteres"
            setSearch('');
        }
    }

    const clearOptions = () => {
        handleSetCompanies([], true)
    }

    const searchCompanies = () => {
        const arrayFilters = fields.map(field => [`[${field}][contain]=${search}`]);
        getCompanies(arrayFilters);
    };

    const getCompanies = arrayFilters => {
        const stringFilters = createFilterQueryString(arrayFilters);
        const limitParam = `per_page=${MAX_RESULTS_PER_PAGE}`;
        const url = `/company?${stringFilters}${limitParam}`;

        if (window.requestController) {
            window.requestController.abort();
        }

        window.requestController = new AbortController();
        handleSetCompanies([], true);
        setNoOptionsText('Carregando...');

        api.get(url, {
            signal: window.requestController.signal
        }).then(res => {
            const raw = res.data?.resources?.data ?? res.data?.resources ?? res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
            const list = Array.isArray(raw) ? raw : [];
            const normalized = list
                .map(item => ({
                    id: item.id,
                    trade_name: item.trade_name ?? item.tradeName ?? item.name ?? '',
                    cnpj: item.cnpj ?? ''
                }))
                .slice(0, MAX_RESULTS_PER_PAGE);
            handleSetCompanies(normalized);
        }).catch(error => {
            if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                setNoOptionsText('Erro ao carregar. Tente novamente.');
                if (error.code !== undefined) {
                    renderError(error);
                }
            }
        });
    }

    const handleSetCompanies = (resources, fromClear = false) => {
        let currentSelectedCompanies = getSelectedCompanies();

        let toCompanies = resources.filter(resource => {
            return !currentSelectedCompanies.some(current => current.id === resource.id);
        });
        setCompanies([...currentSelectedCompanies, ...toCompanies]);
        if(!fromClear){
            setNoOptionsText(toCompanies.length === 0 ? 'Nenhum resultado encontrado' : '');
        }
    }

    const getSelectedCompanies = () => {
        return companies.filter(company => {
            if(isMulti){
                return selectedCompany.includes(company.id);
            } else {
                return selectedCompany === company.id;
            }
        });
    }

    const selectCreated = company => {
        setShowCreate(false);
        setSelectedCompany(isMulti ? [...selectedCompany, company.id] : company.id);
    }

    return (
        <>
            {create &&
                <ModalCreateCompany
                    {...createModalProps(create)}
                    show={showCreate}
                    close={() => setShowCreate(false)}
                    companyStored={selectCreated}
                    showSuccess={false}
                />
            }
            <InputSelect
                noOptionsText={noOptionsText}
                label={label ?? `Empresa`}
                options={createOptions(companies, fields, 'id', false)} 
                value={selectedCompany} 
                change={setSelectedCompany}
                isMulti={isMulti === true}
                setSearch={value => setSearch(value)}
                onCreate={create ? () => setShowCreate(true) : undefined}
            />
        </>
    );
}

export default SelectCompany;